import { DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { CLOUD_FUNCTION_URL_EUW2 } from '../../../env';
import { generateUuidV4 } from '../../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { log } from '../../../util/log';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';

/**
 * Handles the recovery and resetting of user password if forgotten.
 * @param {(): void} returnToSignIn Allows user to return to Sign In modal once password has been reset.
 * @returns {JSX.Element}
 */
const RecoverAccount = ({ returnToSignIn }: { returnToSignIn: () => void }): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [requestSent, setRequestSent] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');
  const [emailFocused, setEmailFocus] = React.useState(false);
  const [validEmail, setValidEmail] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [errorStatus, setErrorStatus] = React.useState<boolean>(false);
  const [resetLoad, setResetLoad] = React.useState<boolean>(false);

  // Handles the triggering of the forgot password email action.
  const handleSendRecoveryEmail = async (): Promise<void> => {
    try {
      setResetLoad(true);
      const response = await fetch(`${CLOUD_FUNCTION_URL_EUW2}/sendPasswordResetEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metadata: { source: 'RecoverAccount' },
          data: { params: { email } }
        })
      });
      if (!response.ok) {
        setErrorStatus(true);
        setErrorMessage('Failed to send recovery email.');
      } else {
        setRequestSent(true);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: RecoverAccount failed on an unknown error while calling handleSendRecoveryEmail.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to send recovery email. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      setResetLoad(false);
    } finally {
      setResetLoad(false);
    }
  };

  // Handles debounce time of user entering the email into the modal and checking for a valid email address.
  React.useEffect(() => {
    setTimeout(() => {
      setValidEmail(email.includes('@'));
    }, 500);
    return () => {
      null;
    };
  }, [email, emailFocused]);

  return (
    <Box>
      <DialogTitle>Account Recovery</DialogTitle>
      {!requestSent && (
        <>
          <DialogContent>
            <DialogContentText>Enter Email to send recovery link to</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              error={!validEmail && emailFocused}
              id="text"
              label="Recovery Email"
              type="text"
              fullWidth
              variant="standard"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.currentTarget.value);
                setEmailFocus(true);
              }}
              value={email}
              sx={{ my: 4 }}
            />
            <DialogContentText sx={{ color: 'red', mt: 1, fontWeight: 'bold' }}>
              {errorStatus && errorMessage}
            </DialogContentText>
          </DialogContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 6,
              gap: 1,
              mx: -2
            }}
          >
            <PrimaryButton
              loading={resetLoad}
              data-testid="send-button"
              onClick={handleSendRecoveryEmail}
              sx={{ justifySelf: 'center' }}
              disabled={!validEmail}
            >
              Send Recovery Email
            </PrimaryButton>
            <GhostButton outlined dark id="back-button" onClick={() => returnToSignIn()}>
              Return to Sign In
            </GhostButton>
          </Box>
        </>
      )}
      {requestSent && (
        <Box>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <DialogContentText sx={{ mb: 6 }}>
              Recovery Email has been sent to specified email if account exists
            </DialogContentText>
            <GhostButton outlined dark id="back-button" onClick={() => returnToSignIn()}>
              Return to Sign In
            </GhostButton>
          </DialogContent>
        </Box>
      )}
    </Box>
  );
};

export default RecoverAccount;
